import React, { useState, useEffect } from 'react';
import { db, auth, appId } from '../firebase-config';
import { collection, query, onSnapshot, doc, updateDoc, serverTimestamp, getDocs, addDoc } from 'firebase/firestore';
import TaskItem from './TaskItem';
import TaskModal from './TaskModal';
import { PlusIcon } from './Icons';
import toast from 'react-hot-toast';

const Tarefas = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const userId = auth.currentUser?.uid;

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    setLoading(true);

    const fetchAllTasks = async () => {
      const processosRef = collection(db, `artifacts/${appId}/users/${userId}/processos`);
      const processosSnap = await getDocs(processosRef);
      const taskPromises = [];

      processosSnap.forEach(processoDoc => {
        const tasksCollectionRef = collection(db, processoDoc.ref.path, 'tasks');
        taskPromises.push(getDocs(tasksCollectionRef));
      });

      const generalTasksRef = collection(db, `artifacts/${appId}/users/${userId}/tasks`);
      taskPromises.push(getDocs(generalTasksRef));

      const allSnapshots = await Promise.all(taskPromises);
      let allTasks = [];
      allSnapshots.forEach(snapshot => {
        const tasksFromSnapshot = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id, path: doc.ref.path }));
        allTasks = [...allTasks, ...tasksFromSnapshot];
      });

      setTasks(allTasks.filter(task => !task.deleted));
      setLoading(false);
    };

    fetchAllTasks().catch(error => {
      console.error("Erro detalhado ao carregar tarefas:", error);
      toast.error("Não foi possível carregar as tarefas.");
      setLoading(false);
    });

  }, [userId, appId]);

  const handleSaveTask = async (taskData) => {
    try {
      if (editingTask) {
        const taskRef = doc(db, editingTask.path);
        await updateDoc(taskRef, { ...taskData, updatedAt: serverTimestamp() });
        toast.success("Tarefa atualizada com sucesso!");
      } else {
        const tasksCollection = collection(db, `artifacts/${appId}/users/${userId}/tasks`);
        await addDoc(tasksCollection, {
          ...taskData,
          userId,
          completed: false,
          createdAt: serverTimestamp(),
        });
        toast.success("Tarefa criada com sucesso!");
      }
    } catch (error) {
       toast.error("Ocorreu um erro ao salvar a tarefa.");
    } finally {
        setIsModalOpen(false);
        setEditingTask(null);
    }
  };

  const handleToggleTask = async (task) => {
    const taskRef = doc(db, task.path);
    await updateDoc(taskRef, { completed: !task.completed, completedAt: !task.completed ? serverTimestamp() : null });
  };
  
  const handleDeleteTask = async (task) => {
      const taskRef = doc(db, task.path);
      await updateDoc(taskRef, { deleted: true, deletedAt: serverTimestamp() });
      toast.success("Tarefa movida para a lixeira!");
  };
  
  const handleEditTask = (task) => {
      setEditingTask(task);
      setIsModalOpen(true);
  };

  const filteredTasks = tasks.filter(task => {
    if (filter === 'pending') return !task.completed;
    if (filter === 'completed') return task.completed;
    return true;
  }).sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));

  if (loading) {
    return <div className="flex justify-center items-center h-full"><div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-gold"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="heading-font text-3xl font-bold text-white">Minhas Tarefas</h1>
          <p className="mt-1 text-gray-300">Organize todas as suas tarefas num único lugar.</p>
        </div>
         <button onClick={() => { setEditingTask(null); setIsModalOpen(true); }} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center space-x-2">
            <PlusIcon className="h-5 w-5" />
            <span>Nova Tarefa</span>
        </button>
      </div>

      <div className="flex space-x-2 p-1 rounded-lg bg-slate-800 w-fit">
        <button onClick={() => setFilter('pending')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${filter === 'pending' ? 'bg-primary-navy text-white' : 'text-gray-400 hover:text-white'}`}>Pendentes ({tasks.filter(t => !t.completed).length})</button>
        <button onClick={() => setFilter('completed')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${filter === 'completed' ? 'bg-primary-navy text-white' : 'text-gray-400 hover:text-white'}`}>Concluídas ({tasks.filter(t => t.completed).length})</button>
        <button onClick={() => setFilter('all')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${filter === 'all' ? 'bg-primary-navy text-white' : 'text-gray-400 hover:text-white'}`}>Todas ({tasks.length})</button>
      </div>

      <div className="space-y-4">
        {filteredTasks.length > 0 ? (
          filteredTasks.map((task) => (
            <TaskItem key={task.id} task={task} onToggle={handleToggleTask} onEdit={handleEditTask} onDelete={handleDeleteTask} />
          ))
        ) : (
          <div className="text-center py-16 glass-effect rounded-lg">
            <i className="fas fa-check-circle text-5xl text-green-500 mb-4"></i>
            <h3 className="text-xl font-semibold text-white mb-2">
              {filter === 'completed' ? 'Nenhuma tarefa concluída' : 'Tudo em ordem!'}
            </h3>
            <p className="text-gray-400">
              {filter === 'completed' 
                ? 'Quando concluir tarefas, elas aparecerão aqui.'
                : 'Você não tem tarefas pendentes. Crie uma nova para começar.'
              }
            </p>
          </div>
        )}
      </div>
      
      <TaskModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSave={handleSaveTask} taskToEdit={editingTask} />
    </div>
  );
};

export default Tarefas;